import { Effect } from 'effect';
import { type ComponentType, lazy, type LazyExoticComponent } from 'react';
import { capture } from './posthog';

const PRELOAD_RELOAD_KEY = 'okra:vite-preload-reload-at';
const PRELOAD_RELOAD_COOLDOWN_MS = 60_000;
const PRELOAD_RELOAD_GRACE_MS = 10_000;

let preloadReloadPending = false;

type PreloadErrorEvent = Event & { payload?: unknown };
type StorageLike = Pick<Storage, 'getItem' | 'setItem'>;

interface PreloadRecoveryDependencies {
  now?: () => number;
  storage?: StorageLike;
  reload?: () => void;
  report?: (properties: Record<string, unknown>) => void;
  cooldownMs?: number;
}

export function payloadMessageEffect(payload: unknown): Effect.Effect<string> {
  return Effect.sync(() => {
    if (payload instanceof Error) return payload.message.slice(0, 300);
    if (typeof payload === 'string') return payload.slice(0, 300);
    return 'Dynamic module preload failed';
  });
}

function payloadMessage(payload: unknown): string {
  return Effect.runSync(payloadMessageEffect(payload));
}

/**
 * Recover a stale Vite client once after a deployment replaces hashed chunks.
 * A second failure inside the cooldown is allowed to reach the normal error
 * boundary so a network/ad-blocking problem cannot cause a reload loop.
 */
export function createPreloadErrorHandler(
  dependencies: PreloadRecoveryDependencies = {},
): (event: PreloadErrorEvent) => void {
  const now = dependencies.now ?? Date.now;
  const storage = dependencies.storage ?? window.sessionStorage;
  const reload = dependencies.reload ?? (() => window.location.reload());
  const cooldownMs = dependencies.cooldownMs ?? PRELOAD_RELOAD_COOLDOWN_MS;
  const report = dependencies.report ?? ((properties) => {
    capture('vite_preload_recovery_triggered', properties, {
      transport: 'sendBeacon',
      send_instantly: true,
    });
  });

  return (event) => {
    const attemptedAt = now();
    let previousAttempt = 0;
    try {
      previousAttempt = Number(storage.getItem(PRELOAD_RELOAD_KEY)) || 0;
    } catch {
      // Storage can be disabled. A single in-memory-free reload is still safer
      // than leaving every stale client on a permanently broken lazy route.
    }

    if (previousAttempt > 0 && attemptedAt - previousAttempt < cooldownMs) return;

    try {
      storage.setItem(PRELOAD_RELOAD_KEY, String(attemptedAt));
    } catch {
      // Without a persistent sentinel, reloading could loop forever.
      return;
    }
    event.preventDefault();
    preloadReloadPending = true;
    report({
      message: payloadMessage(event.payload),
      pathname: window.location.pathname,
    });
    reload();
  };
}

/**
 * `preventDefault()` above makes Vite resolve the failed `import()` to
 * `undefined`, so a lazy loader that reads a named export throws a TypeError
 * (`Cannot read properties of undefined (reading 'MergeRunner')`) just before
 * the reload replaces the page. While that reload is pending, keep the
 * Suspense fallback up instead of flashing the error boundary. If the page is
 * still here after the grace period (a cancelled beforeunload prompt), the
 * original failure reaches the error boundary.
 */
export function holdForPreloadReload<T>(
  error: unknown,
  graceMs: number = PRELOAD_RELOAD_GRACE_MS,
): Promise<T> {
  if (!preloadReloadPending) return Promise.reject(error);
  return new Promise<T>((_, reject) => {
    setTimeout(() => reject(error), graceMs);
  });
}

/** `React.lazy` that waits out a pending stale-chunk reload. */
export function lazyWithPreloadRecovery<TProps>(
  load: () => Promise<{ default: ComponentType<TProps> }>,
): LazyExoticComponent<ComponentType<TProps>> {
  return lazy(() => load().catch((error: unknown) => holdForPreloadReload(error)));
}

export function installPreloadErrorRecovery(): void {
  if (typeof window === 'undefined') return;
  window.addEventListener('vite:preloadError', createPreloadErrorHandler());
}
